import React, { useState, useCallback } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

interface BlackbodyCurveProps {
  width?: number;
  height?: number;
}

export const BlackbodyCurve: React.FC<BlackbodyCurveProps> = ({ width = 400, height = 400 }) => {
  const size = Math.min(width, height);
  const [temp, setTemp] = useState(6000);

  const getStarData = (t: number) => {
    if (t < 4000) return { color: '#FF4444', label: 'Red Dwarf Star' };
    if (t < 5000) return { color: '#FF8C00', label: 'Orange Dwarf' };
    if (t < 7500) return { color: '#FFF5E6', label: 'Yellow Star (our Sun)' };
    if (t < 11000) return { color: '#E0F2FE', label: 'White Star' };
    return { color: '#88CCFF', label: 'Blue Giant Star' };
  };

  const { color, label } = getStarData(temp);

  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number) => {
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, size, size);

    const centerY = size / 2;
    const centerX = size / 2;

    const pulse = Math.sin(frameCount * 0.05) * 5;
    const baseRadius = 60 + (temp / 30000) * 40; // Scale radius slightly with temp

    // Convert hex to rgb for glow
    // Quick hack for glow colors based on known palette
    let r=255, g=255, b=255;
    if (color === '#FF4444') { r=255; g=68; b=68; }
    else if (color === '#FF8C00') { r=255; g=140; b=0; }
    else if (color === '#FFF5E6') { r=255; g=245; b=230; }
    else if (color === '#E0F2FE') { r=224; g=242; b=254; }
    else if (color === '#88CCFF') { r=136; g=204; b=255; }

    ctx.save();
    const starGrad = ctx.createRadialGradient(centerX, centerY, baseRadius * 0.2, centerX, centerY, baseRadius + pulse + 30);
    starGrad.addColorStop(0, '#FFFFFF');
    starGrad.addColorStop(0.3, color);
    starGrad.addColorStop(1, `rgba(${r},${g},${b},0)`);
    
    ctx.fillStyle = starGrad;
    ctx.beginPath();
    ctx.arc(centerX, centerY, baseRadius + pulse + 30, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

  }, [size, color, temp]);

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: size, height: size - 120, position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
        <canvas ref={canvasRef} />
      </div>
      
      <div style={{ marginTop: '20px', width: '100%', maxWidth: '300px', textAlign: 'center' }}>
        <input 
          type="range" 
          min="3000" 
          max="30000" 
          step="100"
          value={temp} 
          onChange={(e) => setTemp(Number(e.target.value))}
          style={{ width: '100%', cursor: 'pointer' }}
        />
        <div style={{ marginTop: '12px', fontSize: '24px', fontWeight: 'bold', color: color }}>
          {temp.toLocaleString()} K
        </div>
        <div style={{ marginTop: '4px', fontSize: '16px', color: '#94A3B8' }}>
          {label}
        </div>
      </div>
    </div>
  );
};
